import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, useLocation } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SubscriptionProtectedRoute } from "./components/SubscriptionProtectedRoute";
import { TenantProvider } from "@/contexts/TenantContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { MoneyVisibilityProvider } from "@/contexts/MoneyVisibilityContext";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import ProductDetails from "./pages/ProductDetails";
import Clients from "./pages/Clients";
import ClientDetails from "./pages/ClientDetails";
import Suppliers from "./pages/Suppliers";
import SupplierDetails from "./pages/SupplierDetails";
import ExpenseCategoryDetails from "./pages/ExpenseCategoryDetails";
import ProductCategoryDetails from "./pages/ProductCategoryDetails";
import Caixa from "./pages/Caixa";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import UserDetails from "./pages/UserDetails";
import Roles from "./pages/Roles";
import Permissions from "./pages/Permissions";
import Tenants from "./pages/Tenants";
import TenantDetails from "./pages/TenantDetails";
import TenantDetails2 from "./pages/TenantDetails2";
import Settings from "./pages/Settings";
import CompanySettings from "./pages/CompanySettings";
import ProfileSettings from "./pages/ProfileSettings";
import PaymentsSettings from "./pages/PaymentsSettings";
import IntegrationsSettings from "./pages/IntegrationsSettings";
import IAgentDefaults from "./pages/IAgentDefaults";
import IAgents from "./pages/IAgents";
import RagUpload from "./pages/RagUpload";
import KnowledgeBases from "./pages/KnowledgeBases";
import KnowledgeBaseDocuments from "./pages/KnowledgeBaseDocuments";
import RagSearch from "./pages/RagSearch";
import TrainingIA from "./pages/TrainingIA";
import TrainingIACreate from "./pages/TrainingIACreate";
import AdvancedLogsSettings from "./pages/AdvancedLogsSettings";
import SettingsChatwoot from "./pages/SettingsChatwoot";
import ApiReference from "./pages/ApiReference";
import Auth from "./pages/Auth";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import Chatwoot from "./pages/Chatwoot";
import ChatwootAudit from "./pages/ChatwootAudit";
import ChatwootAccess from "./pages/ChatwootAccess";
import PublicProduct from "./pages/PublicProduct";
import Forms from "./pages/Forms";
import FormEdit from "./pages/FormEdit";
import PDV from "./pages/PDV";
import Categories from "./pages/Categories";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import Subscriptions from "./pages/Subscriptions";
import SubscriptionTransactions from "./pages/SubscriptionTransactions";
import SubscriptionCoupons from "./pages/SubscriptionCoupons";
import SaasOverview from "./pages/SaasOverview";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AIPrivacyPolicy from "./pages/AIPrivacyPolicy";
import { useEffect } from "react";
import { logNavigation } from "@/services/activities";

const queryClient = new QueryClient();

const AppInner = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    const screenSlug = path.replace(/^\//, "");
    logNavigation(path, screenSlug);
  }, [location.pathname]);
  return (
    <>
      <Toaster />
      <Sonner />
      <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/produto/:id" element={<PublicProduct />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
            <Route path="/politica-de-privacidade-ia" element={<AIPrivacyPolicy />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/saas/visao-geral"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SaasOverview />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtos"
              element={
                <ProtectedRoute requiredModule="products">
                  <Layout>
                    <Products />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtos/:id"
              element={
                <ProtectedRoute requiredModule="products">
                  <Layout>
                    <ProductDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientes"
              element={
                <ProtectedRoute requiredModule="clients">
                  <Layout>
                    <Clients />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientes/:id"
              element={
                <ProtectedRoute requiredModule="clients">
                  <Layout>
                    <ClientDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fornecedores"
              element={
                <ProtectedRoute requiredModule="suppliers">
                  <Layout>
                    <Suppliers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fornecedores/:id"
              element={
                <ProtectedRoute requiredModule="suppliers">
                  <Layout>
                    <SupplierDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias-despesa/:id"
              element={
                <ProtectedRoute requiredModule="transactions">
                  <Layout>
                    <ExpenseCategoryDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias-produto/:id"
              element={
                <ProtectedRoute requiredModule="products">
                  <Layout>
                    <ProductCategoryDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/caixa"
              element={
                <ProtectedRoute requiredModule="transactions">
                  <SubscriptionProtectedRoute>
                    <Layout>
                      <Caixa />
                    </Layout>
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pdv"
              element={
                <ProtectedRoute requiredModule="transactions">
                  <SubscriptionProtectedRoute>
                    <Layout>
                      <PDV />
                    </Layout>
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/relatorios"
              element={
                <ProtectedRoute requiredModule="reports">
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios"
              element={
                <ProtectedRoute requiredModule="users">
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios/:id"
              element={
                <ProtectedRoute requiredModule="users">
                  <Layout>
                    <UserDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/perfis"
              element={
                <ProtectedRoute requiredModule="roles">
                  <Layout>
                    <Roles />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/permissoes"
              element={
                <ProtectedRoute requiredModule="permissions">
                  <Layout>
                    <Permissions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientes-saas"
              element={
                <ProtectedRoute requiredModule="tenants">
                  <Layout>
                    <Tenants />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientes-saas/:id"
              element={
                <ProtectedRoute requiredModule="tenants">
                  <Layout>
                    <TenantDetails2 />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants2/:id"
              element={
                <ProtectedRoute requiredModule="tenants">
                  <Layout>
                    <TenantDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/empresa"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CompanySettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/perfil"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProfileSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/contas-bancarias"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PaymentsSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/integracoes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <IntegrationsSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/ia-defaults"
              element={
                <ProtectedRoute>
                  <Layout>
                    <IAgentDefaults />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/api-reference"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ApiReference />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ia/ia-agentes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <IAgents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ia/treinamento-ia"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TrainingIA />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ia/treinamento-ia/novo"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TrainingIACreate />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ia/treinamento-ia/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TrainingIACreate />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/rag"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RagUpload />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/rag-bases"
              element={
                <ProtectedRoute>
                  <Layout>
                    <KnowledgeBases />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/rag-documentos"
              element={
                <ProtectedRoute>
                  <Layout>
                    <KnowledgeBaseDocuments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/rag-busca"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RagSearch />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/chatwoot"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SettingsChatwoot />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/logs-avancados"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdvancedLogsSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Categories />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cupons"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SubscriptionCoupons />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/assinaturas"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Subscriptions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transacoes"
              element={
                <ProtectedRoute requiredModule="tenants">
                  <Layout>
                    <SubscriptionTransactions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/assinatura"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SubscriptionManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/formularios"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Forms />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/formularios/novo"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FormEdit />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/formularios/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FormEdit />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chatwoot"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Chatwoot />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chatwoot/abrir"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChatwootAccess />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chatwoot/auditoria"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChatwootAudit />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <TenantProvider>
        <ThemeProvider>
          <MoneyVisibilityProvider>
            <AppInner />
          </MoneyVisibilityProvider>
        </ThemeProvider>
      </TenantProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
